import { PayloadAction } from "@reduxjs/toolkit";
import {
  BoardTemplateEnum,
  BoardType,
  FormError,
  IBoard,
  IBoardFixedTemp,
  IBoardSetting,
  ITeamSettings,
  IUserTeamsProjects,
} from "app/types";
import { createSlice } from "utils/@reduxjs/toolkit";
import {
  AuthorizedAccount,
  IUserBasicDetails,
  IUserData,
  IUserTeams,
  PLAN_TYPE,
  User,
  userOnboardingStage,
} from "../types";
import { initialState } from "./initialState";
import toastService from "utils/toast";
import { GroupByTypeEnum } from "app/containers/Features/containers/Boards/types";

const useClinicSlice = createSlice({
  name: "appHomeState",
  initialState,
  reducers: {
    doGetMyProfile: (state) => {},

    setUserFormData: (state, action: PayloadAction<IUserData>) => {
      state.userFormData = action.payload;
    },
    setSelectedTeam: (state, action: PayloadAction<IUserTeams | null>) => {
      state.selectedTeam = action.payload;
    },
    setSelectedProject: (state, action: PayloadAction<string>) => {
      var newData = state.selectedTeam?.projects.filter(
        (x) => x._id == action.payload
      );
      state.selectedProject =
        newData && newData.length > 0
          ? (newData[0] as IUserTeamsProjects)
          : null;
    },
    setUserWorkspaceData: (state, action: PayloadAction<IUserBasicDetails>) => {
      state.userDetails = action.payload;
      state.selectedTeam = action.payload.teams[0];
    },
    doGetUserWorkspace: (
      state,
      action: PayloadAction<{ callback: () => void }>
    ) => {},
    setIsBoardCreate: (state, action: PayloadAction<boolean>) => {
      state.isBoardCreate = action.payload;
    },
    setTeamSetting: (state, action: PayloadAction<ITeamSettings>) => {
      if (!action.payload) return;

      const clonedPayload = {
        ...action.payload,
        ticketStatus: action.payload.ticketTypes?.[0]?.ticketStatuses || [],
        tagList: action.payload.projects?.[0]?.tags || [],
      };

      state.teamSetting = clonedPayload;
    },
    updateBoardsLocally: (state, action: PayloadAction<any>) => {
      
      state.teamSetting.projects[0].boards.push({
        _id: action.payload._id,
        name: action.payload.name,
        settings: {
          viewType: action.payload.viewType,
          Type: action.payload.Type,
        },
        order:
          state.teamSetting.projects[0].boards.length > 4
            ? 5
            : action.payload.order,
      });
    },
    doGetTeamsSetting: (state, action: PayloadAction<{ data: string[] }>) => {},
    setIsBoardLoading: (state, action: PayloadAction<boolean>) => {
      state.isBoardLoading = action.payload;
    },
    doGetSelectProjectSetting: (state, action: PayloadAction<string>) => {
      const selectedData = state.teamSetting.projects.filter(
        (x) => x._id === action.payload
      );
      state.selectedProjectSetting = selectedData;
    },

    // board create API
    doCreateFixTempBoard: (
      state,
      action: PayloadAction<{
        data: IBoardFixedTemp;
        callback: (id: string, viewType: BoardTemplateEnum) => void;
      }>
    ) => {},

    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setAuthorizedAccounts: (
      state,
      action: PayloadAction<AuthorizedAccount[]>
    ) => {
      state.authorizedAccounts = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.loading = false;
    },
    openDrawer(state, action: PayloadAction<any>) {
      state.drawerOpen = true;
      state.drawerPayload = action.payload || null;
    },
    closeDrawer(state) {
      state.drawerOpen = false;
      state.drawerPayload = null;
    },
    fetchUser(state) {
      state.loading = true;
      state.error = null;
    },
    fetchUserFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setTeamFormValue: (
      state,
      action: PayloadAction<{ name: string; value: string }>
    ) => {
      state.teamForm[action.payload.name].value = action.payload.value;
      state.teamForm[action.payload.name].error = "";
    },
    setTeamPlan: (state, action: PayloadAction<{ value: PLAN_TYPE }>) => {
      state.teamForm.plan = action.payload.value;
    },
    setTeamFormErrors: (state, action: PayloadAction<FormError[]>) => {
      action.payload.forEach((error) => {
        state.teamForm[error.name].error = error.error;
      });
    },
    setTeamProjectName: (state, action: PayloadAction<{ value: string }>) => {
      state.teamForm.project.name.value = action.payload.value;
      state.teamForm.project.name.error = "";
    },
    setTeamProjectDefaultCustomization: (
      state,
      action: PayloadAction<{ name: string; value: boolean }>
    ) => {
      state.teamForm.project.defaultCustomization[action.payload.name] =
        action.payload.value;
    },
    setTeamProjectNameError: (
      state,
      action: PayloadAction<{ error: string }>
    ) => {
      state.teamForm.project.name.error = action.payload.error;
    },
    addTeamMember: (state) => {
      state.teamForm.members = [
        ...state.teamForm.members,
        {
          name: {
            value: "",
            error: "",
          },
          role: {
            value: "",
            error: "",
          },
        },
      ];
    },
    setTeamCoupon: (state, action: PayloadAction<{ value: string }>) => {
      state.teamForm.coupon.value = action.payload.value;
      state.teamForm.coupon.error = "";
    },
    removeTeamMember: (state, action: PayloadAction<{ index: number }>) => {
      state.teamForm.members = state.teamForm.members.filter(
        (_, index) => index !== action.payload.index
      );
    },
    updateTeamMember: (
      state,
      action: PayloadAction<{ index: number; name: string; value: string }>
    ) => {
      state.teamForm.members[action.payload.index][action.payload.name].value =
        action.payload.value;
      state.teamForm.members[action.payload.index][action.payload.name].error =
        "";
    },
    updateTeamMemberError: (
      state,
      action: PayloadAction<{ index: number; name: string; error: string }>
    ) => {
      state.teamForm.members[action.payload.index][action.payload.name].error =
        action.payload.error;
    },
    saveTeamRequest: (
      state,
      action: PayloadAction<{ callback?: () => void }>
    ) => {
      state.teamForm.isLoading = true;
    },
    saveTeamRequestComplete: (state) => {
      state.teamForm.isLoading = false;
    },
    nextStep: (state) => {
      state.teamFormActiveIndex = Math.min(state.teamFormActiveIndex + 1, 4);
    },
    previousStep: (state) => {
      state.teamFormActiveIndex = Math.max(state.teamFormActiveIndex - 1, 1);
    },
    setTeamFormActiveIndex: (state, action: PayloadAction<number>) => {
      state.teamFormActiveIndex = action.payload;
    },

    setUserOnboardingProgress: (
      state,
      action: PayloadAction<userOnboardingStage>
    ) => {
      state.userOnboardingProgress = action.payload;
    },
    setUserToken: (state, action: PayloadAction<string>) => {
      state.access_token = action.payload;
    },
    onLogout: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;
      state.access_token = null;
      state.user = null;
      localStorage.removeItem("accessToken");
    },

    setStatusForSelectedTicketType: (
      state,
      action: PayloadAction<{ ticketTypeId: string }>
    ) => {
      const { ticketTypeId } = action.payload;
      const ticketType = state.teamSetting.ticketTypes.find(
        (type) => type._id === ticketTypeId
      );

      if (!ticketType) {
        state.teamSetting.ticketStatus = [];
        return;
      }
      const validStatuses = ticketType.ticketStatuses.filter(
        (status: { ticketTypes: string[] }) =>
          status.ticketTypes.includes(ticketTypeId)
      );
      state.teamSetting.ticketStatus = validStatuses;

      console.log("Valid Statuses: ", validStatuses);
    },
    setAllTicket: (state, action: PayloadAction<any>) => {
      state.teamSetting.ticketList = action.payload;
    },
    setTicketLocally: (
      state,
      action: PayloadAction<{
        ticket: any;
        groupId: string;
      }>
    ) => {
      const { ticket, groupId } = action.payload;

      const ticketList = state.teamSetting.ticketList ?? [];

      const ticketIndex = ticketList.findIndex(
        (t) => t?._id === ticket?._id
      );

      if (ticketIndex !== -1) {
        ticketList[ticketIndex] = {
          ...ticket,
          groupId, // merge or override groupId
        };
      } else {
        ticketList.push({
          ...ticket,
          groupId,
        });
      }

      state.teamSetting.ticketList = [...ticketList];
    },
    setTagLoading: (state, action: PayloadAction<boolean>) => {
      state.tagLoader = action.payload;
    },
    createTagRequest: (
      state,
      action: PayloadAction<{ data; callback: () => void }>
    ) => {
      state.tagLoader = true;
    },
    createTagLocally: (state, action: PayloadAction<any>) => {
      state.teamSetting.tagList.push({
        _id: action?.payload?._id,
        title: action?.payload?.title,
        color: action?.payload?.color,
      });
    },

    updateBoardOrderState: (
      state,
      action: PayloadAction<{ selectedId: string; clickedId: string }>
    ) => {
      const { selectedId, clickedId } = action.payload;

      const boards = state.teamSetting?.projects[0].boards;
      if (!boards) return;
      const selectedIndex = boards.findIndex((card) => card._id === selectedId);
      if (
        !selectedId ||
        boards[selectedIndex].settings.Type === BoardType.Backlog
      ) {
        const clickedIndex = boards.findIndex((card) => card._id === clickedId);
        boards[clickedIndex].order = 1;
        boards.forEach((element) => {
          if (element.settings.Type !== BoardType.Backlog) element.order += 1;
        });
        state.teamSetting.projects[0].boards = boards;
        state.boards = boards.filter((board) => board.order || board._id);
      } else {
        const firstIndex = boards.findIndex((card) => card._id === selectedId);
        const secondIndex = boards.findIndex((card) => card._id === clickedId);

        if (firstIndex === -1 || secondIndex === -1) return;

        // Swap order
        const temp = boards[firstIndex].order;
        boards[firstIndex].order = boards[secondIndex].order;
        boards[secondIndex].order = temp;
        state.teamSetting.projects[0].boards = boards;

        state.boards = boards.filter((board) => board.order || board._id);
      }
    },
    updateNewBoardOrderState: (
      state,
      action: PayloadAction<{ selectedId: string; clickedId: string }>
    ) => {
      const { clickedId } = action.payload;
      const boards = state.teamSetting?.projects[0].boards;
      if (!boards) return;
      if (boards?.length >= 5) {
        const clickedIndex = boards.findIndex((x) => x._id === clickedId);
        boards.forEach((element) => {
          if (element.settings.Type !== BoardType.Backlog && element.order > 4)
            element.order += 1;
        });
        boards[clickedIndex].order = 5;
        state.teamSetting.projects[0].boards = boards.sort(
          (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
        );
      }
    },
    doUpdateBoardOrder: (state) => {},
    boardArchive: (
      state,
      action: PayloadAction<{ id: string; callback: () => void }>
    ) => {
      const boards = state.teamSetting?.projects[0].boards;
      const filterBoards = boards.filter(
        (board) => board._id === action.payload.id
      );
      if (!boards) return;
      const archivedBoards = boards.filter(
        (board) => board._id !== action.payload.id
      );
      state.teamSetting.projects[0].boards = archivedBoards;
      toastService.success(
        `${filterBoards[0]?.name} has been successfully archived.`
      );
    },
     updateBoardGroup: (
      state,
      action: PayloadAction<{
        boardId: string;
        groupBy: string;
        groups: IBoardSetting[];
      }>
    ) => {
      const { boardId, groupBy, groups } = action.payload;
      const teamSetting = state.teamSetting;

      if (!teamSetting?.projects?.[0]?.boards) return;

      const boards = teamSetting.projects[0].boards;

      boards.forEach((board) => {
        if (board._id === boardId) {
          board.settings.groupBy = groupBy as GroupByTypeEnum;
          board.settings.groups = groups;
        }
      });
    }

  },
});

export const { reducer, actions, name: sliceKey } = useClinicSlice;

// i just update due to my better understating
